<template>
    <v-container fluid class="page-wrap pa-0 bg-background">

        <FullPageLoader :visible="isLoading" />

        <!-- HERO -->
        <section fluid class="hero-section py-8 text-center mb-4">
            <v-container class="hero-inner" max-width="1200">
                <h1 class="hero-title">
                    Book Your Adventure
                </h1>
            </v-container>
        </section>

        <!-- MAIN CONTENT -->
        <v-container class="content-inner py-10 px-6 px-md-10" max-width="1200">
            <div class="two-rail">
                <!-- LEFT: SUMMARY + PREFERENCES -->
                <aside class="left-panel">
                    <v-img :src="hero" alt="Cruise image" height="180" cover class="left-hero" />

                    <div class="left-content">
                        <div class="left-title text-h6 font-weight-bold mb-3">
                            {{ title }}
                        </div>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Leaving from</v-col>
                            <v-col cols="7" class="value">{{ departFrom }}</v-col>
                        </v-row>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Onboard</v-col>
                            <v-col cols="7" class="value">{{ shipName }}</v-col>
                        </v-row>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Dates</v-col>
                            <v-col cols="7" class="value d-flex align-center">
                                <span>{{ startDateFmt }}</span>
                                <v-icon size="18" class="mx-2">mdi-arrow-right</v-icon>
                                <span>{{ endDateFmt }}</span>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4" />

                        <!-- Guests & room -->
                        <div class="text-subtitle-1 font-weight-bold mb-2">
                            Guest Info
                        </div>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Guests</v-col>
                            <v-col cols="7" class="value">{{ guestsLabel }}</v-col>
                        </v-row>

                        <!-- SELECTED CABIN INFO FROM PREVIOUS PAGE -->
                        <template v-if="hasCabinInfo">
                            <v-divider class="my-4" />

                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                Selected cabin
                            </div>

                            <v-row dense class="mb-1" v-if="draft?.selectedRoomType">
                                <v-col cols="5" class="label">Room type</v-col>
                                <v-col cols="7" class="value">
                                    {{ draft.selectedRoomType }}
                                </v-col>
                            </v-row>

                            <v-row dense class="mb-1" v-if="draft?.cabinNumber">
                                <v-col cols="5" class="label">Cabin</v-col>
                                <v-col cols="7" class="value">
                                    {{ draft.cabinNumber }}
                                </v-col>
                            </v-row>

                            <v-row dense class="mb-1" v-if="draft?.selectedCabinDeck">
                                <v-col cols="5" class="label">Deck</v-col>
                                <v-col cols="7" class="value">
                                    {{ draft.selectedCabinDeck }}
                                </v-col>
                            </v-row>
                        </template>



                        <v-divider class="my-4" />

                        <!-- PREFERENCES SUMMARY -->
                        <div class="text-subtitle-1 font-weight-bold mb-2">
                            Preferences
                        </div>

                        <v-row dense class="mb-1">
                            <v-col cols="6" class="label">Dining time</v-col>
                            <v-col cols="6" class="value">
                                {{ draft.preferences.diningTime  || 'Not selected' }}
                            </v-col>
                        </v-row>

                        <v-row dense>
                            <v-col cols="6" class="label">Bed setup</v-col>
                            <v-col cols="6" class="value">
                                {{ draft.preferences.bed  || 'Not selected' }}
                            </v-col>
                        </v-row>

                        <!-- PRICING SUMMARY (BOTTOM SECTION) -->
                        <div v-if="hasPriceInfo" class="pricing-summary mt-4 pt-3">
                            <v-divider class="mb-4" />

                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                Trip total
                            </div>

                            <v-row dense class="mb-1" v-if="draft?.selectedCabinPrice">
                                <v-col cols="5" class="label">Total</v-col>
                                <v-col cols="7" class="value">
                                    {{ currency(draft.selectedCabinPrice) }}
                                </v-col>
                            </v-row>

                            <v-row dense class="mb-1" v-if="draft?.selectedCabinDeposit">
                                <v-col cols="5" class="label">Deposit</v-col>
                                <v-col cols="7" class="value">
                                    {{ currency(draft.selectedCabinDeposit) }}
                                </v-col>
                            </v-row>

                            <v-row dense v-if="draft?.selectedCabinTipsIncluded !== undefined">
                                <v-col cols="5" class="label">Tips included</v-col>
                                <v-col cols="7" class="value">
                                    {{ draft.selectedCabinTipsIncluded ? 'Yes' : 'No' }}
                                </v-col>
                            </v-row>
                        </div>
                    </div>
                </aside>

                <!-- RIGHT: PREFERENCES CARDS -->
                <section class="right-rail" v-if="cruise">
                    <BookingStepper :active-step="activeStep" class="mb-6" />

                    <header class="mb-5">
                        <h2 class="text-h4 font-weight-bold mb-1">Who's traveling?</h2>
                        <p class="text-body-2 text-grey-darken-1">
                            The details you provide for all guests should match their government-issued photo IDs.
                        </p>
                    </header>

                    <v-card elevation="2" class="guest-card">
                        <v-card-title class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">
                                    Guest details
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                    Guest {{ activeGuestTab + 1 }} of {{ guests.length }}
                                </div>
                            </div>

                            <v-chip size="small" color="primary" variant="elevated">
                                {{ guests.length }} Guest{{ guests.length > 1 ? 's' : '' }}
                            </v-chip>
                        </v-card-title>

                        <v-divider />

                        <!-- TABS -->
                        <v-card-text class="pb-0">
                            <v-tabs v-model="activeGuestTab" grow density="comfortable" class="guest-tabs">
                                <v-tab v-for="(guest, index) in guests" :key="index" :value="index" class="guest-tab">
                                    <div class="d-flex flex-column align-center">
                                        <span class="text-caption text-medium-emphasis mb-1">
                                            Guest {{ index + 1 }}
                                        </span>
                                    </div>
                                </v-tab>
                            </v-tabs>
                        </v-card-text>

                        <v-divider />

                        <!-- TAB CONTENT -->
                        <v-card-text>
                            <v-window v-model="activeGuestTab">
                                <v-window-item v-for="(guest, index) in guests" :key="index" :value="index">
                                    <v-row dense class="mb-2">
                                        <v-col cols="12">
                                            <div class="d-flex align-center justify-space-between mb-2">
                                                <span class="text-subtitle-2 font-weight-bold">
                                                    Guest {{ index + 1 }}
                                                </span>
                                                <v-chip v-if="!guestIsComplete(guest)" size="small" color="red"
                                                    class="ms-3" label>
                                                    Incomplete
                                                </v-chip>
                                                <v-chip v-else size="small" color="green" variant="elevated" label>
                                                    Ready
                                                </v-chip>
                                            </div>
                                        </v-col>
                                    </v-row>

                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="guest.firstName" :rules="[r.required]"
                                                label="First Legal Name" variant="outlined" density="comfortable" />
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="guest.lastName" :rules="[r.required]"
                                                label="Last Legal Name" variant="outlined" density="comfortable" />
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-select v-model="guest.gender" :rules="[r.required]"
                                                :items="['Male', 'Female']" label="Gender" variant="outlined"
                                                density="comfortable" clearable />
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-menu v-model="dobMenu" :close-on-content-click="false"
                                                transition="scale-transition" offset-y min-width="290px">
                                                <template #activator="{ props }">
                                                    <v-text-field v-bind="props" v-model="guest.dob"
                                                        :rules="[r.required]" label="Date of Birth" variant="outlined"
                                                        density="comfortable" prepend-inner-icon="mdi-calendar"
                                                        readonly />
                                                </template>

                                                <v-date-picker v-model="guest.dob" color="primary" hide-header
                                                    :allowed-dates="isAllowedDate" @update:model-value="dobMenu = false"
                                                    show-adjacent-months />
                                            </v-menu>
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-combobox v-model="guest.citizenship" :items="countryOptions"
                                                label="Citizenship" variant="outlined" density="comfortable" clearable
                                                @update:model-value="val => addOptionIfNew('countryOptions', val)" />
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-combobox v-model="guest.language" :items="languageOptions"
                                                label="Language (optional)" variant="outlined" density="comfortable"
                                                clearable
                                                @update:model-value="val => addOptionIfNew('languageOptions', val)" />
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="guest.loyalty" label="Loyalty Number (optional)"
                                                variant="outlined" density="comfortable" />
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                            </v-window>
                        </v-card-text>
                    </v-card>

                    <!-- BACK + CONTINUE -->
                    <div class="d-flex justify-space-between align-center mt-4 bottom-actions">
                        <v-btn color="primary" variant="outlined" size="large" class="px-8"
                            prepend-icon="mdi-arrow-left" @click="goBack">
                            Back
                        </v-btn>

                        <v-btn color="primary" size="large" rounded="lg" class="px-8" :disabled="!isStepValid"
                            @click="continueWithPreferences">
                            Continue
                        </v-btn>
                    </div>
                </section>
            </div>
        </v-container>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import BookingStepper from '@/components/BookingStepper.vue'
import FullPageLoader from '@/components/FullPageLoader.vue'

export default {
    name: 'GuestInfoPage',
    components: {
        BookingStepper,
        FullPageLoader
    },
    props: {
        // Wire this up from your previous steps (party size)
        guestCount: {
            type: Number,
            default: 2
        }
    },
    data() {
        return {
            activeStep: 3, // 0 = guests, 1 = cabin, 2 = preferences
            cruiseId: null,
            listingID: null,
            cruise: null,
            draft: null,
            loading: true,

            activeGuestTab: 0,
            guests: [],
            countryOptions: ['United States', 'Canada'],
            languageOptions: ['English', 'Spanish'],

            r: {
                required: v => !!v || 'Required'
            },

            dobMenu: false

        }
    },
    async created() {
        this.cruiseId = this.$route.params.id
        this.listingID = this.$route.params.listing

        // Get existing draft from previous steps
        this.draft = this.$route.state?.bookingDraft
            || JSON.parse(sessionStorage.getItem('bookingDraft') || 'null')

        if (!this.draft) {
            // user skipped earlier steps; send back to start
            this.$router.replace({ name: 'DealDetail', params: { id: this.cruiseId } })
            return
        }

        if (this.draft.preferences) {
            this.diningTime = this.draft.preferences.diningTime || null
            this.bed = this.draft.preferences.bed || null
        } else {
            this.goBack()
            return
        }

        // If the user already picked prefs, preload them
        if (this.draft.guestInfo) {
            this.guests = this.draft.guestInfo
        }

        await this.fetchCruise()
        this.loading = false
    },
    computed: {
        isLoading() {
            return this.cruise == null
        },
        allGuestsComplete() {
            return this.guests.length > 0 && this.guests.every(g => this.guestIsComplete(g))
        },
        isStepValid() {
            return !!this.diningTime && !!this.bed
        },
        hero() { return this.cruise?.full_image_url || this.cruise?.imgSrc || '' },
        title() { return this.cruise?.title || '—' },
        departFrom() { return this.cruise?.departure_port || '—' },
        shipName() { return this.cruise?.ship_name || '—' },
        numberOfNights() {
            const m = (this.cruise?.title || '').match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        startDateFmt() {
            const ymd = this.cruise?.date
            if (!ymd) return '—'
            const [y, m, d] = ymd.split('-').map(Number)
            const dt = new Date(y, m - 1, d)
            return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        },
        endDateFmt() {
            if (this.cruise?.end_date) {
                const [y, m, d] = this.cruise.end_date.split('-').map(Number)
                return new Date(y, m - 1, d).toLocaleDateString(
                    'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' }
                )
            }
            if (this.cruise?.date && this.numberOfNights) {
                const [y, m, d] = this.cruise.date.split('-').map(Number)
                const dt = new Date(y, m - 1, d)
                dt.setDate(dt.getDate() + this.numberOfNights)
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            }
            return '—'
        },
        guestsLabel() {
            const g = this.draft?.guestsMeta || {}
            const a = g.adults ?? 2
            const c = g.children ?? 0
            const aLabel = `${a} Adult${a > 1 ? 's' : ''}`
            const cLabel = c ? `, ${c} Child${c > 1 ? 'ren' : ''}` : ''
            return aLabel + cLabel
        },
        hasCabinInfo() {
            return !!(
                this.draft?.selectedRoomType ||
                this.draft?.cabinNumber ||
                this.draft?.selectedCabinDeck ||
                this.draft?.selectedCabinPrice
            )
        },
        hasPriceInfo() {
            return !!(
                this.draft?.selectedCabinPrice ||
                this.draft?.selectedCabinDeposit ||
                this.draft?.selectedCabinTipsIncluded !== undefined
            )
        }
    },
    watch: {
        guestCount: {
            immediate: true,
            handler(newVal) {
                this.syncGuestCount(newVal)
            }
        }
    },
    methods: {
        syncGuestCount(count) {
            const current = this.guests.length

            // Add missing guests
            if (count > current) {
                for (let i = current; i < count; i++) {
                    this.guests.push(this.createEmptyGuest())
                }
            }

            // Trim if we have too many
            if (count < current) {
                this.guests.splice(count)
            }

            if (this.activeGuestTab >= this.guests.length) {
                this.activeGuestTab = Math.max(0, this.guests.length - 1)
            }
        },
        createEmptyGuest() {
            return {
                title: '',
                firstName: '',
                middleName: '',
                lastName: '',
                gender: '',
                dob: '',
                citizenship: '',
                language: '',
                loyalty: ''
            }
        },
        guestIsComplete(guest) {
            return (
                !!guest.firstName &&
                !!guest.lastName &&
                !!guest.gender &&
                !!guest.dob &&
                !!guest.citizenship
            )
        },
        // Local YYYY-MM-DD (avoids UTC off-by-one)
        todayIso() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        },

        // Normalize to 'YYYY-MM-DD'
        normalizeToIso(val) {
            if (!val) return "";
            if (typeof val === "string") return val.slice(0, 10);
            const d = new Date(val);
            if (isNaN(d)) return "";
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        },
        isAllowedDate(val) {
            const iso = this.normalizeToIso(val);
            if (!iso) return false;
            if (iso > this.todayIso()) return false;
            return true;
        },
        addOptionIfNew(listName, value) {
            if (!value) return
            const list = this[listName]
            if (Array.isArray(list) && !list.includes(value)) {
                list.push(value)
            }
        },
        async fetchCruise() {
            const { data, error } = await supabase
                .from('cruises')
                .select('*')
                .eq('id', this.cruiseId)
                .single()
            if (!error) this.cruise = data
        },
        currency(val) {
            if (val == null || val === '') return 'N/A'
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
            }).format(val)
        },
        goBack() {
            // persist current preferences into draft before going back
            if (this.draft) {
                const updatedDraft = {
                    ...this.draft,
                    guestInfo: [...this.guests]
                    
                }
                sessionStorage.setItem('bookingDraft', JSON.stringify(updatedDraft))
            }

            // go back to cabin selection step
            this.$router.push({
                name: 'Preferences', // make sure this matches your cabin page route name
                params: { id: this.cruiseId, listing: this.listingID }
            })
        },
    }
}
</script>

<style scoped>
/* MAIN CONTENT WRAP */
.content-inner {
    margin-top: -16px;
    /* slight overlap feeling, same as other pages */
}

/* 2-rail layout */
.two-rail {
    display: flex;
    align-items: flex-start;
    gap: 24px;
}

/* LEFT PANEL */
.left-panel {
    --sticky-offset: 96px;
    flex: 0 0 360px;
    max-width: 360px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    position: sticky;
    top: var(--sticky-offset);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.left-hero {
    flex: 0 0 auto;
}

.left-content {
    padding: 18px 18px 20px;
}

.left-title {
    line-height: 1.3;
}

/* RIGHT RAIL */
.right-rail {
    flex: 1 1 auto;
    min-width: 0;
}

/* labels on left summary */
.label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
}

.value {
    color: rgba(0, 0, 0, 0.87);
}

/* BACK BUTTON */
.back-btn {
    text-transform: none;
    font-weight: 500;
}

/* OPTION CARDS */
.option-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 12px;
}

.option-card {
    border-radius: 12px;
    border: 1px solid #e1e1e1;
    padding: 14px 16px;
    cursor: pointer;
    transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.option-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.option-icon {
    background-color: #f2f3ff;
    color: #2436c8;
}

.option-title-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    gap: 8px;
}

.option-card:hover {
    background-color: #fafafa;
}

.option-card--selected {
    border-color: var(--color-primary, #2436c8);
    background-color: #f4f6ff;
    box-shadow: 0 0 0 1px rgba(36, 54, 200, 0.16);
}

/* MOBILE / TABLET */
@media (max-width: 959.98px) {
    .option-grid {
        grid-template-columns: 1fr;
        /* stack on mobile */
    }
}


/* PRICE SECTION AT BOTTOM */
.pricing-section {
    border-top: 1px solid #eee;
    padding-top: 12px;
    margin-top: 16px;
    padding-bottom: 4px;
}

.pricing-header {
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #666;
}

.pricing-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
}

.pricing-label {
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.65);
}

.pricing-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
}

.pricing-section--empty .pricing-row {
    justify-content: flex-start;
}

/* MOBILE / TABLET */
@media (max-width: 959.98px) {
    .two-rail {
        flex-direction: column;
    }

    .left-panel {
        position: static;
        max-width: 100%;
    }

    .content-inner {
        padding-inline: 16px !important;
    }

    .option-grid {
        flex-direction: column;
    }
}
</style>
